import React, { ChangeEvent, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../EntityEditor.styles";
import StyledTextField from "../../styled-components/StyledTextField";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PostcodeForm, { PostcodeFormHandle } from '../../postcode-form/PostcodeForm';
import { LatinTextField } from '../../latin-text-field/LatinTextField';
import AlertService from '../../../services/notification.service';
import { NumericTextField } from '../../numeric-text-field/NumericTextField';
import CityAutocomplete  from '../../city-autocomplete/CityAutocomplete';

const initialEditModeState = {
  isEdit: false,
  index: -1,
};

export const initialErrorEditState = {
  type: false,
  postcode: false,
  city_name: false,
  street_name: false,
  number: false,
  number_add: false,
}

export const initialAddressState = {
  type: "",
  postcode: "",
  city_name: "",
  street_name: "",
  number: "",
  number_add: "",
  municipality: "",
  city_code: "",
};

export type EntityAddressBlockHandle = {
  reset: () => void;
}

interface EntityAddressBlockProps  {
  entityAddresses?: any,
  onChange?: any,
  typeEdit?: any,
}

const initialAddressDetails = {
  type: "",
  postcode: "",
  city_name: "",
  street_name: "",
  number: "",
  number_add: "",
  municipality: "",
  city_code: "",
  index: -1,
  id: "",
  modified: false,
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EntityAddressBlock = React.forwardRef<EntityAddressBlockHandle, EntityAddressBlockProps>(({ entityAddresses, onChange, typeEdit}, ref ) => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(initialEditModeState);
  /**current address which we type into fields**/
  const [entityAddress, setEntityAddress] = useState(initialAddressState);
  /**address list from props which we save in state**/
  const [entityAddressList, setEntityAddressList] = useState([]);
  /**details for update directly in fields**/
  const [editedAddressDetails, setEditedAddressDetails] = useState(
    initialAddressDetails
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [addressForDelete, setAddressForDelete] = useState([]);
  const [errorFields, setErrorFields] = useState(initialErrorEditState);
  const [errorAddressFields, setErrorAddressFields] = useState(initialErrorEditState);
  const [inputValue, setInputValue ] = useState("");
  const [inputValueEdit, setInputValueEdit] = useState("");
  const [check, setCheck] = useState<boolean>(true);
  const postcodeRef = React.useRef<PostcodeFormHandle | null>(null);
  // const [isFocused, setIsFocused] = useState(false);
  const type = "entity";

  useEffect(() => {
    setEntityAddressList(entityAddresses);
  }, [dispatch, entityAddresses]);

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setEditedAddressDetails(initialAddressDetails);
    setEntityAddress(initialAddressState);
    setEditMode(initialEditModeState);
    setAddressForDelete([]);
    setEntityAddressList([]);
    setInputValue("");
    setErrorFields(initialErrorEditState);
    setErrorAddressFields(initialErrorEditState);
  };

  const reset = useCallback(() => {
    handleClearForm();
    postcodeRef.current?.reset();
  }, []);
  useImperativeHandle(ref, () => ({reset}), [reset]);

  const handleChange = (
    _name: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEntityAddress({
      ...entityAddress,
      [key]: event.target.value,
    });
  };

  const handleBlurItem = (
    fieldName: string,
    _selectedIndex: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (fieldName === "postcode") {
      if ( event.target.value === "" && check) {
        AlertService.warning(`Please fill in the field ${fieldName}.`);
        setErrorFields?.(prev => ({...prev, [fieldName]: true}));
      } else {
        setErrorFields?.(prev => ({...prev, [fieldName]: false}));
      }
    }
  }
  const handleChangeItem = (
    fieldName: string,
    selectedIndex: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEditedAddressDetails({
      ...editedAddressDetails,
      [fieldName]: event.target.value,
      index: selectedIndex,
      modified: true,
    });
  };

  const handleDeleteItem = (selectedIndex: number) => {
    const newStateForDelete = [];
    entityAddressList.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });

    const filteredList = entityAddressList.filter(
      (_item: any, index) => index !== selectedIndex
    );

    const newState = {
      filteredList,
      newStateForDelete,
    };

    setEntityAddressList(filteredList);
    setAddressForDelete(newStateForDelete);
    onChange("entity_addresses", newState, true);
  };

  const handleSetEditMode = (
    type: string,
    selectedIndex: number,
  ) => {
    /** managing open/close state for ip item change, save changes by click in ip list **/
    if (type === "open") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setCheck(true);
      setErrorFields(initialErrorEditState);
      entityAddressList.forEach((item: any, index) => {
        if (index === selectedIndex) {
          setEditedAddressDetails({
            ...item,
            index: selectedIndex,
            id: item.id || null,
            type: item.type,
            postcode: item.postcode,
            city_name: item.city_name,
            street_name: item.street_name,
            number: item.number,
            number_add: item.number_add,
            municipality: item.municipality,
            city_code: item.city_code,
          });
        }
      });
    }
    if (type === "close") {
      setCheck(false);
      setErrorFields(initialErrorEditState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });

    }
    if (type === "edit") {
      if (!editedAddressDetails.city_name ) {
        const fieldName = "city_name"
        setErrorFields?.(prev => ({ ...prev, [fieldName]: true }));
        return;
      }
      if (Object.values(errorFields).includes(true)) {
        return;
        // AlertService.warning("Fields are not valid.")
      }
      const newState = entityAddressList.map((item: any, index) => {
        if (index === editedAddressDetails.index && index === selectedIndex) {
          return {
            id: editedAddressDetails.id,
            type: editedAddressDetails.type,
            postcode: editedAddressDetails.postcode,
            city_name: editedAddressDetails.city_name,
            street_name: editedAddressDetails.street_name,
            number: editedAddressDetails.number,
            number_add: editedAddressDetails.number_add,
            municipality: editedAddressDetails.municipality,
            city_code: editedAddressDetails.city_code,
            modified: editedAddressDetails.modified,
          };
        } else {
          return item;
        }
      });

      // @ts-ignore
      setEntityAddressList(newState);
      onChange("entity_addresses", newState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      // setEditedAddressDetails(editedAddressDetails);
      setErrorFields(initialErrorEditState);
      setCheck(false);
      setErrorAddressFields(initialErrorEditState);
    }
  };

  const addNewAddress = () => {
    if (entityAddress.city_name && !Object.values(errorAddressFields).includes(true)) {
      const newState = [
        ...entityAddressList,
        {
          type: entityAddress.type,
          postcode: entityAddress.postcode,
          city_name: entityAddress.city_name,
          street_name: entityAddress.street_name,
          number: entityAddress.number,
          number_add: entityAddress.number_add,
          // municipality: entityAddress.municipality,
          // city_code: entityAddress.city_code
        },
      ];
      // @ts-ignore
      setEntityAddressList(newState);
      onChange("entity_addresses", newState);
      handleClearForm();
      setErrorAddressFields(initialErrorEditState);
    } else {
      if (!entityAddress.city_name && !Object.values(errorAddressFields).includes(true)) {
        const fieldName = "city_name"
        setErrorAddressFields?.(prev => ({...prev, [fieldName]: true}));
        AlertService.warning("Fields are not valid.");
      }
    }
  };
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="legend" sx={styles.textField}>
          Addresses
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <PostcodeForm
          ref={postcodeRef}
          selectAddress={setEntityAddress}
          address={entityAddress}
          type={type}
          errorFields={errorAddressFields}
          setErrorFields={setErrorAddressFields}
        />
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <LatinTextField
              fullWidth
              label="Type"
              type="text"
              name="type"
              errorFields={errorAddressFields.type}
              setErrorFields={setErrorAddressFields}
              margin="dense"
              value={entityAddress?.type}
              onChange={(e) => handleChange("entityAddresses", "type", e)}
            />

            <CityAutocomplete
              label="City"
              name="city_name"
              value={entityAddress?.city_name ? entityAddress.city_name : null}
              inputValue={inputValue}
              setInputValue={setInputValue}
              minChars={3}
              debounceMs={300}
              errorFields={errorAddressFields.city_name}
              setErrorFields={setErrorAddressFields}
              textFieldProps={{
                fullWidth: true,
                margin: "dense",
                type: "text",
                required: true,
              }}
              onChange={(e) => handleChange("entityAddresses", "city_name", e)}
            />

            <NumericTextField
              fullWidth
              label="Number"
              type="text"
              name="number"
              errorFields={errorAddressFields.number}
              setErrorFields={setErrorAddressFields}
              margin="dense"
              value={entityAddress?.number}
              onChange={(e) => handleChange("entityAddresses", "number", e)}
            />

          </Box>
          <Box sx={styles.fieldBlock}>
            <StyledTextField
              fullWidth
              label="Postcode"
              error={errorAddressFields.postcode}
              name="postcode"
              margin="dense"
              onChange={(e) => handleChange("entityAddresses", "postcode", e)}
              value={entityAddress?.postcode}
            />

            <LatinTextField
              fullWidth
              label="Street"
              type="text"
              name="street_name"
              errorFields={errorAddressFields.street_name}
              setErrorFields={setErrorAddressFields}
              margin="dense"
              value={entityAddress?.street_name}
              onChange={(e) =>
                handleChange("entityAddresses", "street_name", e)
              }
            />

            <StyledTextField
              fullWidth
              label="Number add"
              margin="dense"
              type="text"
              name="number_add"
              error={errorAddressFields.number_add}
              onChange={(e) => handleChange("entityAddresses", "number_add", e)}
              value={entityAddress?.number_add}
            />
          </Box>
        </Box>
        <Button sx={styles.button} onClick={addNewAddress}>
          Add new address
        </Button>
        <Divider sx={{ marginTop: "15px", marginBottom: "15px" }} />
        {entityAddressList?.length > 0 &&
          entityAddressList.map(
            (
              item: {
                type: string;
                postcode: string;
                city_name: string;
                street_name: string;
                number: string;
                number_add: string;
              },
              index
            ) => {
              return editMode.isEdit && editMode.index === index ? (
                <Box sx={styles.editHolder} key={index}>
                  <Typography variant="body2" sx={styles.textField}>
                    Edit current address
                  </Typography>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <LatinTextField
                        fullWidth
                        variant="standard"
                        label="Type"
                        type="text"
                        name="type"
                        errorFields={errorFields?.type}
                        setErrorFields={setErrorFields}
                        margin="dense"
                        value={editedAddressDetails?.type}
                        onChange={(e) => handleChangeItem("type", index, e)}
                      />

                      <CityAutocomplete
                        label="City"
                        name="city_name"
                        value={editedAddressDetails?.city_name}
                        inputValue={inputValueEdit}
                        setInputValue={setInputValueEdit}
                        minChars={3}
                        debounceMs={300}
                        errorFields={errorFields?.city_name}
                        setErrorFields={setErrorFields}
                        textFieldProps={{
                          fullWidth: true,
                          margin: "dense",
                          type: "text",
                          variant: "standard",
                          required: true,
                        }}
                        onChange={(e) => handleChangeItem("city_name", index, e)}
                      />
                      {/*<LatinTextField*/}
                      {/*  fullWidth*/}
                      {/*  variant="standard"*/}
                      {/*  label="City"*/}
                      {/*  required*/}
                      {/*  check={check}*/}
                      {/*  type="text"*/}
                      {/*  name="city_name"*/}
                      {/*  errorFields={errorFields?.city_name}*/}
                      {/*  setErrorFields={setErrorFields}*/}
                      {/*  margin="dense"*/}
                      {/*  value={editedAddressDetails?.city_name}*/}
                      {/*  onChange={(e) =>*/}
                      {/*    handleChangeItem("city_name", index, e){}*/}
                      {/*/>*/}

                      <NumericTextField
                        fullWidth
                        variant="standard"
                        required
                        label="Number"
                        type="text"
                        check={check}
                        name="number"
                        errorFields={errorFields.number}
                        setErrorFields={setErrorFields}
                        margin="dense"
                        value={editedAddressDetails?.number}
                        onChange={(e) => handleChangeItem("number", index, e)}
                      />

                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <TextField
                        fullWidth
                        variant="standard"
                        label="Postcode"
                        error={errorFields?.postcode}
                        required
                        name="postcode"
                        margin="dense"
                        onBlur={(e) => handleBlurItem("postcode", index, e)}
                        onChange={(e) => handleChangeItem("postcode", index, e)}
                        value={editedAddressDetails.postcode}
                      />

                      <LatinTextField
                        fullWidth
                        variant="standard"
                        label="Street"
                        required
                        check={check}
                        type="text"
                        name="street_name"
                        errorFields={errorFields.street_name}
                        setErrorFields={setErrorFields}
                        margin="dense"
                        value={editedAddressDetails?.street_name}
                        onChange={(e) =>
                          handleChangeItem("street_name", index, e)}
                      />
                      <TextField
                        fullWidth
                        variant="standard"
                        label="Number add"
                        margin="dense"
                        onChange={(e) =>
                          handleChangeItem("number_add", index, e)
                        }
                        value={
                          editedAddressDetails.number_add
                            ? editedAddressDetails.number_add
                            : ""
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={styles.editButtonGroup}>
                    <IconButton
                      type="button"
                      onClick={() => handleSetEditMode("edit", index)}
                    >
                      <DoneIcon fontSize={"small"} />
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => handleSetEditMode("close", index)}
                    >
                      <CloseIcon fontSize={"small"} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.itemContainer} key={index}>
                  <Box sx={styles.itemHolder}>
                    <Box sx={styles.infoHolder}>
                      <Box sx={styles.typeInfo}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Type:
                        </Typography>
                        <Tooltip
                          title={item?.type}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemDetails} variant={"h6"}>
                            {item?.type}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={styles.info}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Address:
                        </Typography>
                        <Tooltip
                          title={`${item.postcode} ${item.city_name}, ${item.street_name} ${item.number} ${item.number_add ?? ""}`}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemInfoDetails} variant={"h6"}>
                            {`${item.postcode} ${item.city_name}, ${item.street_name} ${item.number} ${item.number_add ?? ""}`}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={styles.iconHolder}>
                      <IconButton
                        aria-label="Edit"
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => handleSetEditMode("open", index)}
                      >
                        <EditIcon sx={styles.editIcon} />
                      </IconButton>
                      <IconButton
                        aria-label="Delete"
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon sx={styles.deleteIcon} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
      </AccordionDetails>
    </Accordion>
  );
});

export default EntityAddressBlock;
